import { defineComponent, h, ref, shallowRef, onMounted, onBeforeUnmount } from 'vue';
import type {
  createEvidenceClient,
  EvidenceGraphView,
  EvidenceGraphDetail,
  EvidenceGraphNode,
  EvidenceSource,
} from '@knowyet/sdk/evidence';
import type { PreviewRequest } from '@knowyet/preview';
import {GraphOriginalPreview} from './graph-original-preview.js';
import { pdfOptions } from './preview-options.js';
import { sourcePreview, previewMediaType, RenditionNotice } from './source-preview.js';
import { emptyExploration, mergeExploration, GraphReadQueue, type Edge } from './graph-state.js';
import { GraphScene, kindLabels, edgeLabel, nodeColors } from './graph-scene.js';
export const graphKinds = kindLabels;
const displayName = (node: EvidenceGraphNode) => ['source','document'].includes(node.kind) && node.file ? node.file : Array.from(node.label).slice(0,100).join('');
type Api = ReturnType<typeof createEvidenceClient>;
type Expansion = { loaded: number; total: number | null; more: boolean; ids: Set<string> };
export const GraphPanel = defineComponent({
  emits: ['busy'],
  props: {
    workspace: { type: String, required: true },
    api: { type: Object as () => Api, required: true },
    loadBlob: { type: Function, required: true },
    disabled: Boolean,
  },
  setup(props, { emit }) {
    const query = ref(''),
      kind = ref('object'),
      file = ref(''),
      catalog = shallowRef<EvidenceGraphView>(),
      results = shallowRef<EvidenceGraphNode[]>([]);
    const graph = shallowRef(emptyExploration()),
      detail = shallowRef<EvidenceGraphDetail>(),
      selected = shallowRef<EvidenceSource>(),
      preview = shallowRef<PreviewRequest>();
    const fieldAlternatives = shallowRef<EvidenceSource[]>([]);
    const focus = ref(''), fitRequest = ref(0),
      expansions = shallowRef(new Map<string, Expansion>()),
      roots = new Set<string>();
    const error = ref(''),
      activity = ref(''),
      pending = ref(0),
      relationship = shallowRef<Edge>();
    const queue = new GraphReadQueue(),
      control = new AbortController();
    let disposed = false,
      clickTimer: ReturnType<typeof setTimeout> | undefined;
    let applied = { query: '', query_mode: 'name' as const, kind: 'object', file: '' };
    const run = (label: string, fn: () => Promise<void>) => {
      pending.value++;
      emit('busy', true);
      void queue
        .run(async () => {
          activity.value = label;
          error.value = '';
          await fn();
        })
        .catch((e) => {
          if (!disposed) error.value = String(e);
        })
        .finally(() => {
          pending.value--;
          if (!disposed) {
            emit('busy', pending.value > 0);
            if (!pending.value) activity.value = '';
          }
        });
    };
    function browse(reset = true) {
      if (props.disabled || pending.value) return;
      const filter = reset
        ? { query: query.value, query_mode: 'name' as const, kind: kind.value, file: file.value }
        : applied;
      run('正在查找实体…', async () => {
        const view = await props.api.graph(
          props.workspace,
          {
            ...filter,
            limit: 30,
            offset: reset ? 0 : results.value.length,
            ...(!reset ? { snapshot_id: graph.value.snapshot } : {}),
          },
          control.signal,
        );
        if (graph.value.snapshot && graph.value.snapshot !== view.snapshot_id) {
          graph.value = emptyExploration();
          expansions.value = new Map();
          roots.clear();
        }
        graph.value = { ...graph.value, snapshot: view.snapshot_id };
        catalog.value = view;
        results.value = reset ? view.nodes : [...results.value, ...view.nodes];
        applied = filter;
      });
    }
    function inspect(id: string) {
      const node = graph.value.nodes.get(id);
      if (!node || props.disabled) return;
      focus.value = id;
      relationship.value = undefined;
      run('正在读取节点…', async () => {
        fieldAlternatives.value=[];
        detail.value = await props.api.graphNode(props.workspace, id, graph.value.snapshot, control.signal);
        selected.value = undefined;
        preview.value = undefined;
      });
    }
    function add(node: EvidenceGraphNode) {
      roots.add(node.id);
      graph.value = mergeExploration(graph.value, { snapshot_id: graph.value.snapshot!, nodes: [node], edges: [] });
      inspect(node.id);
    }
    function expand(id: string) {
      clearTimeout(clickTimer);
      if (props.disabled) return;
      focus.value = id;
      run('正在展开一跳邻居…', async () => {
        const known = expansions.value.get(id);
        if (known && !known.more) return;
        const view = await props.api.graph(
          props.workspace,
          { center: id, offset: known?.loaded ?? 0, limit: 100, snapshot_id: graph.value.snapshot },
          control.signal,
        );
        graph.value = mergeExploration(graph.value, view, id);
        const ids = new Set([...(known?.ids ?? []), ...view.nodes.filter((n) => n.id !== id).map((n) => n.id)]);
        expansions.value = new Map(expansions.value).set(id, {
          loaded: view.next_offset ?? view.offset + view.nodes.filter((n) => n.id !== id).length,
          total: view.total,
          more: view.has_more,
          ids,
        });
      });
    }
    function remove(ids: Set<string>) {
      const value = graph.value;
      graph.value = {
        ...value,
        nodes: new Map([...value.nodes].filter(([id]) => !ids.has(id))),
        edges: new Map([...value.edges].filter(([, e]) => !ids.has(e.from) && !ids.has(e.to))),
        positions: new Map([...value.positions].filter(([id]) => !ids.has(id))),
      };
    }
    function collapse() {
      const item = expansions.value.get(focus.value);
      if (!item) return;
      const next = new Map(expansions.value);
      next.delete(focus.value);
      const retain = new Set([...roots, focus.value, ...next.keys(), ...[...next.values()].flatMap((e) => [...e.ids])]);
      remove(new Set([...item.ids].filter((id) => !retain.has(id))));
      expansions.value = next;
    }
    function showSource(source: EvidenceSource) {
      selected.value = source;
      preview.value = sourcePreview(source, (ref, signal) => props.loadBlob(ref, AbortSignal.any([signal, control.signal])));
    }
    function lineage() {
      if (!detail.value?.lineage) return;
      graph.value = mergeExploration(
        graph.value,
        { snapshot_id: detail.value.snapshot_id, ...detail.value.lineage },
        focus.value,
      );
      fitRequest.value++;
    }
    onMounted(() => browse());
    onBeforeUnmount(() => {
      disposed = true;
      clearTimeout(clickTimer);
      queue.close();
      control.abort();
      emit('busy', false);
    });
    const button = (label: string, fn: () => void, disabled = false, attrs = {}) =>
      h('button', { type: 'button', onClick: fn, disabled: disabled || props.disabled, ...attrs }, label);
    return () => {
      const nodes = [...graph.value.nodes.values()].map(n => ({...n,label:displayName(n)})),
        edges = [...graph.value.edges.values()],
        expanded = expansions.value.get(focus.value),
        body = detail.value?.body;
      return h('section', { class: 'card graph-panel', 'data-testid': 'graph-panel' }, [
        h('div', { class: 'section-title' }, [h('h2', '图谱探索'), h('span', '搜索 → 展开 → 查看依据')]),
        h(
          'form',
          {
            class: 'graph-searchbar',
            onSubmit: (e: Event) => {
              e.preventDefault();
              browse();
            },
          },
          [
            h('input', {
              'aria-label': '实体名称',
              placeholder: '搜索实体名称，例如公司、人物、日期…',
              value: query.value,
              onInput: (e: Event) => (query.value = (e.target as HTMLInputElement).value),
            }),
            h(
              'select',
              {
                'aria-label': '节点类型',
                value: kind.value,
                onChange: (e: Event) => (kind.value = (e.target as HTMLSelectElement).value),
              },
              ['object', 'record', 'dataset', 'schema', 'unit', 'chunk', 'document', 'source'].map((k) => h('option', { value: k }, kindLabels[k])),
            ),
            h(
              'select',
              {
                'aria-label': '来源文件',
                value: file.value,
                onChange: (e: Event) => (file.value = (e.target as HTMLSelectElement).value),
              },
              [
                h('option', { value: '' }, '全部材料'),
                ...(catalog.value?.files ?? []).map((f) => h('option', { value: f }, f)),
              ],
            ),
            h('button', { class: 'primary', disabled: !!pending.value || props.disabled }, '查找节点'),
          ],
        ),
        h('div', { class: 'graph-summary' }, [
          h('span', { 'data-testid': 'graph-matches' }, `匹配 ${results.value.length} / ${catalog.value?.total ?? 0}`),
          h(
            'strong',
            { 'data-testid': 'graph-count', 'data-count': nodes.length },
            `画布 ${nodes.length} 个节点 · ${edges.length} 条关系`,
          ),
          button('清空画布', () => {
            graph.value = { ...emptyExploration(), snapshot: graph.value.snapshot };
            roots.clear();
            expansions.value = new Map();
            focus.value = '';
            detail.value = undefined;
          }),
        ]),
        h(
          'div',
          { class: 'graph-activity', role: 'status', 'data-testid': 'graph-status' },
          activity.value || '双击节点展开邻居 · 拖动节点调整位置 · 滚轮缩放 · 拖动空白平移',
        ),
        error.value ? h('p', { class: 'error', role: 'alert' }, error.value) : null,
        h('div', { class: 'explorer-layout' }, [
          h('aside', { class: 'graph-results' }, [
            h('h3', '搜索结果'),
            ...results.value.map((n) =>
              h(
                'button',
                {
                  type: 'button',
                  onClick: () => add(n),
                  disabled: !!pending.value || props.disabled,
                  'data-result-id': n.id,
                  class: 'graph-result',
                },
                [h('span', n.label), h('small', n.file ?? '')],
              ),
            ),
            catalog.value?.has_more ? button('更多匹配', () => browse(false), !!pending.value) : null,
          ]),
          h('div', { class: 'graph-stage' }, [
            h(GraphScene, {
              nodes,
              edges,
              focus: focus.value,
              fitRequest: fitRequest.value,
              onSelect: (id: string) => {
                clearTimeout(clickTimer);
                clickTimer = setTimeout(() => inspect(id), 260);
              },
              onExpand: expand,
              onEdge: (e: Edge) => {
                relationship.value = e;
              },
            }),
            h(
              'div',
              { class: 'graph-legend' },
              [...new Set(nodes.map((n) => n.kind))].map((k) =>
                h('span', [h('i', { style: { background: nodeColors[k] || '#bac5ce' } }), kindLabels[k] || k]),
              ),
            ),
          ]),
          h('aside', { class: 'graph-inspector', 'data-testid': 'graph-detail' }, [
            relationship.value
              ? h('div', [
                  h('h3', '关系'),
                  h('p', edgeLabel(relationship.value)),
                  h(
                    'p',
                    `${graph.value.nodes.get(relationship.value.from)?.label} → ${graph.value.nodes.get(relationship.value.to)?.label}`,
                  ),
                ])
              : null,
            focus.value
              ? h('div', { class: 'graph-walk', 'data-testid': 'graph-walk' }, [
                  h('h3', graph.value.nodes.has(focus.value) ? displayName(graph.value.nodes.get(focus.value)!) : ''),
                  h(
                    'p',
                    { 'data-testid': 'neighbor-count' },
                    expanded ? `已展开 ${expanded.ids.size} 个邻居${expanded.total == null ? (expanded.more ? '，可继续展开' : '，已全部展开') : ` / ${expanded.total}`}` : '选择一个动作开始探索',
                  ),
                  button(
                    expanded?.more ? '加载下一页邻居' : expanded ? '邻居已全部展开' : '展开一跳邻居',
                    () => expand(focus.value),
                    !!pending.value || (!!expanded && !expanded.more),
                  ),
                  expanded ? button('收起本次展开', collapse, !!pending.value) : null,
                  button(
                    '移出画布',
                    () => {
                      remove(new Set([focus.value]));
                      roots.delete(focus.value);
                      expansions.value.delete(focus.value);
                      focus.value = '';
                      detail.value = undefined;
                    },
                    !!pending.value,
                  ),
                ])
              : h('p', { class: 'muted' }, '选择搜索结果加入画布，点击节点或关系查看详情。'),
            detail.value
              ? h('div', [
                  h('p', { class: 'muted' }, detail.value.node.file ?? ''),
                  Array.isArray(body?.properties)
                    ? h(
                        'dl',
                        (body.properties as { name: string; value: string }[]).flatMap((p) => [
                          h('dt', p.name),
                          h('dd', p.value),
                        ]),
                      )
                    : null,
                  Array.isArray(body?.trace_fields) ? h('div', {'data-testid':'graph-fields'},
                    (body.trace_fields as {name:string;value:unknown;sources:EvidenceSource[]}[]).map(field =>
                      h('div', [h('span', `${field.name}：${String(field.value ?? '空值')} `),
                        button('查看原件并高亮', () => {
                          fieldAlternatives.value=field.sources;
                          if(field.sources.length) showSource(field.sources[0]);
                        }, !field.sources.length || !!pending.value,
                        {'data-testid':'graph-field-source','data-field':field.name}),
                        !field.sources.length ? h('small','此字段没有可高亮的原文位置') : null]))) : null,
                  typeof body?.index_text === 'string' ? h('p', body.index_text) : null,
                  button('在图中查看来源链', lineage, !!pending.value),
                  h('h4', '原文依据'),
                  h('p', { 'data-testid': 'graph-source-count' }, `${detail.value.sources.length} 个引用片段`),
                  ...detail.value.sources.map((source, i) =>
                    button(
                      `${i + 1}. ${source.file} · ${source.text.slice(0, 80)}`,
                      () => { fieldAlternatives.value=[]; showSource(source); },
                      !!pending.value,
                      { class: 'source', 'data-source-id': source.original.id },
                    ),
                  ),
                  detail.value.source_note ? h('p', { class: 'muted' }, detail.value.source_note) : null,
                ])
              : null,
          ]),
        ]),
        selected.value && preview.value
          ? h(
              'div',
              { class: 'source-modal', role: 'dialog', 'aria-label': '原文依据', 'data-testid': 'graph-preview' },
              [
                h('div', { class: 'source-modal-header' }, [
                  h('h3', selected.value.file),
                  button('关闭原文', () => {
                    selected.value = undefined;
                    preview.value = undefined;
                  }),
                ]),
                fieldAlternatives.value.length>1 ? h('nav', {'aria-label':'该字段的所有来源'},fieldAlternatives.value.map((source,i)=>
                  button(`来源 ${i+1} · ${source.file}`,()=>showSource(source),false,{'data-testid':'field-source-alternative'}))) : null,
                h('blockquote', selected.value.text),
                h(RenditionNotice, {source:selected.value, loadOriginal:(signal:AbortSignal)=>props.loadBlob(selected.value!.original,AbortSignal.any([signal,control.signal]))}),
                h(GraphOriginalPreview, {
                  source:selected.value,
                  request: preview.value,
                  mediaType: previewMediaType(selected.value),
                  locations: selected.value.locations,
                  pdfOptions,
                }),
              ],
            )
          : null,
      ]);
    };
  },
});
